import * as React from 'react'
import styled from 'styled-typed'
import { History } from 'history'
import { Navigation } from 'navigation'
import { match as RouteMatch } from 'react-router-dom'
import { delay } from 'utils'
import { ActionButton, ActionButtonA } from 'uiComponents/buttons'
import { withNavigation } from 'hocs'
import { withFeatures } from 'features'
import { ButtonWrapper, TextWithHorizontalLine } from 'uiComponents/pageElements'
import { MessageKind } from 'uiComponents/messages'
import { ContainerBody } from 'uiComponents/settingsContainer'
import { TableLoader, ChartDataLoader } from 'uiComponents/loaders'
import { Col, Row } from 'uiComponents/flex'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { SingleSelect, TextInput, NumberInput } from 'uiComponents/input'
import { InventoryServiceContext } from 'inventory/context'
import { InventoryRule, InventoryItem } from 'inventory/inventoryService'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { PageActions } from 'uiComponents/settingsContainer'
import { RulesList } from 'inventory/rulesList'
import Infotip from 'uiComponents/infotip'

const Section = styled(Col)`
    margin-bottom: 2em;
`

interface InventoryFormParams {
    accountSlug: string
    id: string
}

interface TimeSlotGroupFormProps {
    accountSlug: string
    match: RouteMatch<InventoryFormParams>
    history: History
    navigation: Navigation
    setActiveSection: (section: string, header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
    hasFeature: (feature: string, accountSlug: string) => boolean
}

function InventoryForm(props: TimeSlotGroupFormProps) {
    const didMountRef = React.useRef(false)
    const inventoryService = React.useContext(InventoryServiceContext)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [updating, setUpdating] = React.useState<boolean>(false)
    const [name, setName] = React.useState<string>('')
    const [capacity, setCapacity] = React.useState<number | null | undefined>(undefined)
    const [rulesList, setRulesList] = React.useState<InventoryRule[]>([])
    const [validate, setValidate] = React.useState<boolean>(false)
    const [type, setType] = React.useState<'Venue' | 'Gate'>('Venue')

    const fetchInventoryData = async (id: string) => {
        try {
            setLoading(true)
            const data = await inventoryService.getInventoryDetails(props.accountSlug, id)
            setDataToState(data)
            setRulesList(data.availabilityRules)
        } catch {
            props.replaceMessages('server_error', 'error', 'Oops! Something went wrong. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const setDataToState = (data: InventoryItem) => {
        setName(data.name)
        setCapacity(data.defaultAvailability)
        setType(data.reservationPeriodType)
    }

    React.useEffect(() => {
        const inventoryId = props.match.params.id
        props.setActiveSection('inventory', inventoryId === 'new' ? 'Add New Inventory' : 'Inventory Configuration')
        if (!!inventoryId && inventoryId !== 'new') {
            fetchInventoryData(inventoryId)
        }
    }, [])

    React.useEffect(() => {
        if (didMountRef.current) {
            props.history.push(`/account/${props.accountSlug}/products/inventory/home`)
        } else {
            didMountRef.current = true
        }
    }, [props.accountSlug])

    const onCapacityChange = (value: string) => {
        setCapacity(value ? Number(value) : null)
    }

    const isFormValid = () => {
        setValidate(true)
        return !!name
    }

    const onInventorySave = async () => {
        if (isFormValid()) {
            try {
                setUpdating(true)
                const payload = {
                    id: props.match.params.id,
                    name,
                    account: props.accountSlug,
                    defaultAvailability: capacity,
                    reservationPeriodType: type,
                }
                if (props.match.params.id !== 'new') {
                    const savedInventory = await inventoryService.updateInventoryItem(props.accountSlug, payload)
                    setDataToState(savedInventory)
                } else {
                    const savedInventory = await inventoryService.createInventoryItem(props.accountSlug, payload)
                    const id = savedInventory.id
                    props.setActiveSection('inventory', 'Inventory Configuration')
                    props.history.push(`/account/${props.accountSlug}/products/inventory/item/${id}`)
                }
                setUpdating(false)
                props.replaceMessages('success', 'success', 'The inventory capacity has been saved successfully.')
                await delay(3000)
                props.removeAllMessages()
            } catch {
                setUpdating(false)
                props.replaceMessages(
                    'server_error',
                    'error',
                    'Oops! Could not save the inventory capacity, please try again later.',
                )
            }
        }
    }

    return (
        <div style={{ position: 'relative' }}>
            {loading && <TableLoader style={{ minHeight: '41em' }} />}
            {updating && <ChartDataLoader />}
            {!loading && (
                <ContainerBody id="inventory-form" style={{ flexDirection: 'column' }}>
                    <Row>
                        <Section span={6}>
                            <FormItem htmlFor="inventory-name" style={{ position: 'relative' }}>
                                <FormItemName>Inventory name</FormItemName>
                                <TextInput
                                    id="inventory-name"
                                    name="inventory-name"
                                    type="text"
                                    maxLength={255}
                                    value={name}
                                    placeholder="Add inventory name..."
                                    onChange={(e) => setName(e.target.value)}
                                    status={validate && !name ? 'error' : 'normal'}
                                    block
                                />
                            </FormItem>
                            {validate && !name && (
                                <ValidationMessage className="validation-message-visible">
                                    This field is required
                                </ValidationMessage>
                            )}
                        </Section>
                        <Section span={6}>
                            <FormItem htmlFor="timeSlot-name" style={{ position: 'relative' }}>
                                <FormItemName>
                                    Default capacity
                                    <span style={{ opacity: 0.5 }}>&nbsp;(optional)</span>
                                    <Infotip pointer="left">Leave blank for an unlimited capacity</Infotip>
                                </FormItemName>
                                <NumberInput
                                    id="default-capacity"
                                    name="default-capacity"
                                    integerOnly
                                    min={0}
                                    max={100000}
                                    value={capacity !== null ? capacity : ''}
                                    placeholder=" "
                                    onChange={(e) => onCapacityChange(e.target.value)}
                                    block
                                />
                            </FormItem>
                        </Section>
                    </Row>
                    <Row>
                        <Section span={6}>
                            <FormItem htmlFor="type">
                                <FormItemName>Inventory type</FormItemName>
                            </FormItem>
                            <SingleSelect
                                id="type"
                                options={[
                                    { name: 'Venue', value: 'Venue' },
                                    { name: 'Gate', value: 'Gate' },
                                ]}
                                noSelectOption="Select search by"
                                selected={type}
                                onSelect={(v) => setType(v as 'Venue' | 'Gate')}
                            />
                        </Section>
                        <Section span={6} />
                    </Row>
                    <Col span={12}>
                        <ButtonWrapper>
                            <ActionButtonA
                                size="large"
                                href={`/account/${props.accountSlug}/products/inventory/home`}
                                secondary
                                style={{ marginRight: '1.5em' }}
                                kind="action"
                            >
                                Cancel
                            </ActionButtonA>
                            <ActionButton id="save-inventory" size="large" onClick={onInventorySave}>
                                Save
                            </ActionButton>
                        </ButtonWrapper>
                    </Col>
                    {props.match.params.id !== 'new' && (
                        <>
                            <TextWithHorizontalLine text="Inventory rules" alignment="left" />
                            {rulesList.length > 0 && (
                                <RulesList
                                    accountSlug={props.accountSlug}
                                    rulesList={rulesList}
                                    inventoryId={props.match.params.id}
                                    hasFeature={props.hasFeature}
                                />
                            )}
                            {rulesList.length === 0 && (
                                <NoResultsRow text="This inventory configuration has no rules" />
                            )}
                            <PageActions>
                                <ActionButtonA
                                    id="newRule"
                                    size="large"
                                    href={`/account/${props.accountSlug}/products/inventory/rule/new/item/${props.match.params.id}`}
                                >
                                    Add rule
                                </ActionButtonA>
                            </PageActions>
                        </>
                    )}
                </ContainerBody>
            )}
        </div>
    )
}

export default withFeatures(withNavigation(InventoryForm))
